import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { money } from "@/lib/format";
import { useBrandLogos } from "@/lib/brandLogos";
import { DiscountBadge } from "@/components/ProductCard";

export function NewArrivalsCarousel({ products }) {
  const [idx, setIdx] = useState(0);
  const { logoFor } = useBrandLogos();

  useEffect(() => {
    if (products.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % products.length), 5000);
    return () => clearInterval(t);
  }, [products.length]);

  if (!products.length) return null;
  const p = products[idx % products.length];
  const img = p.images?.[0]?.url;
  const rrpVerified = p.rrp_verified !== false && !!p.original_rrp;
  const savingsAmount = rrpVerified ? Math.round(p.original_rrp - p.selling_price) : null;
  const savingsPct = rrpVerified ? Math.round(((p.original_rrp - p.selling_price) / p.original_rrp) * 100) : null;
  const logoUrl = logoFor(p.brand_slug);
  const goTo = (n) => setIdx((n + products.length) % products.length);

  return (
    <div className="relative bg-[#F8F8F9] rounded-3xl overflow-hidden" data-testid="new-arrivals-carousel">
      <AnimatePresence mode="wait">
        <motion.div
          key={p.id}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-2 items-center"
          data-testid={`new-arrivals-slide-${idx}`}
        >
          <Link to={`/product/${p.slug || p.id}`} className="relative aspect-[3/2] flex items-center justify-center overflow-hidden">
            <div className="absolute top-6 left-6 z-10">
              <DiscountBadge pct={savingsPct} amount={savingsAmount} />
            </div>
            <img src={img} alt={`${p.brand_name} ${p.model}`} className="w-full h-full object-contain scale-150" />
          </Link>
          <div className="p-8 sm:p-12 lg:pr-16">
            {logoUrl ? (
              <img src={logoUrl} alt={p.brand_name} className="h-7 w-auto object-contain mb-4" />
            ) : (
              <div className="font-extrabold uppercase tracking-widest text-lg mb-4 text-neutral-900">{p.brand_name}</div>
            )}
            <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-neutral-900 mb-4">{p.model}</h3>
            <div className="flex items-baseline gap-3 mb-4 flex-wrap">
              <span className="text-3xl sm:text-4xl font-mono font-extrabold text-neutral-900" data-testid="new-arrivals-carousel-price">{money(p.selling_price)}</span>
              {rrpVerified && <span className="text-base font-mono text-neutral-400 line-through">{money(p.original_rrp)}</span>}
            </div>
            <div className="flex items-center gap-2 mb-6" data-testid="new-arrivals-carousel-condition">
              <div className="flex gap-1">
                {Array.from({ length: 10 }).map((_, i) => (
                  <span key={i} className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: i < p.condition_score ? `rgba(9, 184, 223, ${0.2 + (i / 9) * 0.8})` : "#E4E4E7" }} />
                ))}
              </div>
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-neutral-500">{p.condition_score}/10 {p.condition_label}</span>
            </div>
            <Link to={`/product/${p.slug || p.id}`} className="inline-flex items-center gap-2 bg-charcoal text-white font-heading font-bold text-sm uppercase tracking-wide px-7 py-3.5 rounded-full hover:bg-teal-600 transition-all hover:scale-[1.02] active:scale-95" data-testid="new-arrivals-carousel-view">
              View Details <ArrowUpRight size={16} />
            </Link>
          </div>
        </motion.div>
      </AnimatePresence>

      <button onClick={() => goTo(idx - 1)} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors" data-testid="new-arrivals-prev" aria-label="Previous frame">
        <ChevronLeft size={18} />
      </button>
      <button onClick={() => goTo(idx + 1)} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 shadow-md flex items-center justify-center hover:bg-white transition-colors" data-testid="new-arrivals-next" aria-label="Next frame">
        <ChevronRight size={18} />
      </button>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="new-arrivals-dots">
        {products.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all ${i === idx % products.length ? "w-6 bg-charcoal" : "w-2 bg-neutral-300"}`}
            data-testid={`new-arrivals-dot-${i}`}
            aria-label={`Go to frame ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
